"use client";

import { c as _c } from "react/compiler-runtime";
import { ShieldExclamationIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { CreateWaveGroupSearchResultsLayout } from "@/components/waves/create-wave/groups/CreateWaveGroupSearchResults";
import CreateWaveInlineGroupDraftSummary from "@/components/waves/create-wave/groups/CreateWaveInlineGroupDraftSummary";
import CreateWaveInlineGroupExpandedPanel from "@/components/waves/create-wave/groups/CreateWaveInlineGroupExpandedPanel";
import CreateWaveInlineGroupHeader from "@/components/waves/create-wave/groups/CreateWaveInlineGroupHeader";
import CreateWaveInlineGroupIdentities from "@/components/waves/create-wave/groups/CreateWaveInlineGroupIdentities";
import CreateWaveInlineGroupRuleEditor from "@/components/waves/create-wave/groups/CreateWaveInlineGroupRuleEditor";
import { CreateWaveInlineGroupRuleEditorPanel, CreateWaveInlineGroupRuleList } from "@/components/waves/create-wave/groups/CreateWaveInlineGroupRules";
import CreateWaveInlineGroupSearch from "@/components/waves/create-wave/groups/CreateWaveInlineGroupSearch";
import CreateWaveInlineGroupActions from "@/components/waves/create-wave/groups/CreateWaveInlineGroupActions";
import { getInlineGroupRuleCount } from "@/components/waves/create-wave/groups/createWaveInlineGroupBuilder";
import { type CreateWaveGroupInlinePanelProps, useCreateWaveGroupInlinePanel } from "@/components/waves/create-wave/groups/useCreateWaveGroupInlinePanel";
type GroupAssignmentPanelLayout = "inline" | "dialog";
type GroupAssignmentPanelStartMode = "actions" | "existing";
type GroupAssignmentPanelProps = CreateWaveGroupInlinePanelProps & {
  readonly layout?: GroupAssignmentPanelLayout;
  readonly startMode?: GroupAssignmentPanelStartMode;
};
function DialogActionButton(t0) {
  const $ = _c(12);
  const {
    icon,
    label,
    count,
    active,
    disabled,
    onClick
  } = t0;
  const stateClasses = active ? "tw-border-primary-500/50 tw-bg-primary-500/10 tw-text-primary-300" : "tw-border-white/5 tw-bg-iron-900 tw-text-iron-200 desktop-hover:hover:tw-border-white/10 desktop-hover:hover:tw-bg-iron-850 desktop-hover:hover:tw-text-white";
  const t1 = `tw-flex tw-min-h-11 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-2.5 tw-text-sm tw-font-semibold tw-transition tw-duration-200 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${stateClasses}`;
  let t2;
  if ($[0] !== label) {
    t2 = <span>{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== count) {
    t3 = count > 0 ? <span aria-hidden="true" className="tw-rounded-full tw-bg-white/10 tw-px-1.5 tw-py-0.5 tw-text-[0.68rem] tw-font-bold tw-leading-none tw-text-iron-200">{count}</span> : null;
    $[2] = count;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== active || $[5] !== disabled || $[6] !== icon || $[7] !== onClick || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <button type="button" onClick={onClick} disabled={disabled} aria-pressed={active} className={t1}>{icon}{t2}{t3}</button>;
    $[4] = active;
    $[5] = disabled;
    $[6] = icon;
    $[7] = onClick;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function DialogTabs(t0) {
  const $ = _c(11);
  const {
    isExistingActive,
    onExistingClick,
    onNewClick
  } = t0;
  const getTabClasses = _temp;
  const t1 = `tw-rounded-md tw-border-0 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-transition tw-duration-200 ${getTabClasses(isExistingActive)}`;
  let t2;
  if ($[0] !== isExistingActive || $[1] !== onExistingClick || $[2] !== t1) {
    t2 = <button type="button" role="tab" aria-selected={isExistingActive} onClick={onExistingClick} className={t1}>Existing group</button>;
    $[0] = isExistingActive;
    $[1] = onExistingClick;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !isExistingActive;
  const t4 = `tw-rounded-md tw-border-0 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-transition tw-duration-200 ${getTabClasses(!isExistingActive)}`;
  let t5;
  if ($[4] !== onNewClick || $[5] !== t3 || $[6] !== t4) {
    t5 = <button type="button" role="tab" aria-selected={t3} onClick={onNewClick} className={t4}>New group</button>;
    $[4] = onNewClick;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t5) {
    t6 = <div role="tablist" aria-label="Group source" className="tw-grid tw-grid-cols-2 tw-rounded-lg tw-bg-iron-900 tw-p-1">{t2}{t5}</div>;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function _temp(active) {
  return active ? "tw-bg-iron-800 tw-text-iron-50 tw-shadow-sm" : "tw-bg-transparent tw-text-iron-400 desktop-hover:hover:tw-text-iron-100";
}
function DialogGroupSummary(t0) {
  const $ = _c(10);
  const {
    currentGroupLabel,
    unsavedGroupDescription,
    unsavedGroupSummary
  } = t0;
  const summaryGridClasses = unsavedGroupSummary ? "tw-grid tw-gap-3 sm:tw-grid-cols-2" : "tw-grid tw-gap-3";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-500">Current group</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== currentGroupLabel) {
    t2 = <div className="tw-min-w-0 tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-iron-900/70 tw-p-3">{t1}<p className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-100">{currentGroupLabel}</p></div>;
    $[1] = currentGroupLabel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== unsavedGroupDescription || $[4] !== unsavedGroupSummary) {
    t3 = unsavedGroupSummary ? <div className="tw-min-w-0 tw-rounded-lg tw-border tw-border-solid tw-border-primary-500/20 tw-bg-primary-500/5 tw-p-3"><p className="tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-primary-300">Unsaved group</p><p className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-100">{unsavedGroupSummary}</p>{unsavedGroupDescription ? <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-font-medium tw-text-iron-400">{unsavedGroupDescription}</p> : null}</div> : null;
    $[3] = unsavedGroupDescription;
    $[4] = unsavedGroupSummary;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== summaryGridClasses || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className={summaryGridClasses}>{t2}{t3}</div>;
    $[6] = summaryGridClasses;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function DialogNewGroupActions(t0) {
  const $ = _c(15);
  const {
    disabled,
    identityCount,
    identityActive,
    ruleCount,
    ruleActive,
    onAddIdentity,
    onAddRule
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <UserPlusIcon aria-hidden="true" className="tw-size-4 tw-flex-shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== disabled || $[2] !== identityActive || $[3] !== identityCount || $[4] !== onAddIdentity) {
    t2 = <DialogActionButton icon={t1} label="Add identity" count={identityCount} disabled={disabled} active={identityActive} onClick={onAddIdentity} />;
    $[1] = disabled;
    $[2] = identityActive;
    $[3] = identityCount;
    $[4] = onAddIdentity;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ShieldExclamationIcon aria-hidden="true" className="tw-size-4 tw-flex-shrink-0" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== disabled || $[8] !== onAddRule || $[9] !== ruleActive || $[10] !== ruleCount) {
    t4 = <DialogActionButton icon={t3} label="Add rule" count={ruleCount} disabled={disabled} active={ruleActive} onClick={onAddRule} />;
    $[7] = disabled;
    $[8] = onAddRule;
    $[9] = ruleActive;
    $[10] = ruleCount;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className="tw-grid tw-gap-2 sm:tw-grid-cols-2">{t2}{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function renderSearchPanel({
  allowGroupClear,
  defaultLabel,
  disabled,
  hasUnsavedGroup,
  onExistingGroupSelect,
  resultsLayout = "popover",
  selectedGroup
}: {
  readonly allowGroupClear: boolean;
  readonly defaultLabel: string;
  readonly disabled: boolean;
  readonly hasUnsavedGroup: boolean;
  readonly onExistingGroupSelect: (group: ApiGroupFull | null) => void;
  readonly resultsLayout?: CreateWaveGroupSearchResultsLayout;
  readonly selectedGroup: ApiGroupFull | null;
}) {
  return <CreateWaveInlineGroupSearch defaultLabel={defaultLabel} disabled={disabled} hasUnsavedGroup={hasUnsavedGroup} selectedGroup={selectedGroup} allowGroupClear={allowGroupClear} resultsLayout={resultsLayout} onSelect={onExistingGroupSelect} />;
}
export default function GroupAssignmentPanel(t0) {
  const $ = _c(49);
  const {
    layout: t1,
    ...props
  } = t0;
  const layout = t1 === undefined ? "inline" : t1;
  const {
    allowGroupClear: t2,
    defaultLabel,
    disabled: t3,
    selectedGroup
  } = props;
  const allowGroupClear = t2 === undefined ? true : t2;
  const disabled = t3 === undefined ? false : t3;
  const {
    addIdentity,
    canCreateDraft,
    canResetDraft,
    currentGroupLabel,
    displayedBuilder,
    draftSummary,
    hasUnsavedGroup,
    isCreating,
    isDraftValid,
    isExpandedPanel,
    isIdentityPanel,
    isRulePanel,
    isSearchPanel,
    onCancelPanel,
    onClearAll,
    onCreateAndUse,
    onExistingGroupSelect,
    openRule,
    panelRef,
    removeIdentity,
    setDraft,
    showDraftFooter,
    togglePanel,
    toggleRule,
    unsavedGroupDescription,
    unsavedGroupSummary
  } = useCreateWaveGroupInlinePanel(props);
  if (layout === "dialog") {
    let t4;
    if ($[0] !== isSearchPanel || $[1] !== togglePanel) {
      t4 = () => {
        if (!isSearchPanel) {
          togglePanel("search", false);
        }
      };
      $[0] = isSearchPanel;
      $[1] = togglePanel;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    const openExistingTab = t4;
    let t5;
    if ($[3] !== isSearchPanel || $[4] !== togglePanel) {
      t5 = () => {
        if (isSearchPanel) {
          togglePanel("actions", false);
        }
      };
      $[3] = isSearchPanel;
      $[4] = togglePanel;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    const openNewGroupTab = t5;
    const identityCount = displayedBuilder.identities.length;
    let t6;
    if ($[6] !== displayedBuilder.draft) {
      t6 = getInlineGroupRuleCount(displayedBuilder.draft);
      $[6] = displayedBuilder.draft;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    const ruleCount = t6;
    let t7;
    if ($[8] !== currentGroupLabel || $[9] !== unsavedGroupDescription || $[10] !== unsavedGroupSummary) {
      t7 = <DialogGroupSummary currentGroupLabel={currentGroupLabel} unsavedGroupDescription={unsavedGroupDescription} unsavedGroupSummary={unsavedGroupSummary} />;
      $[8] = currentGroupLabel;
      $[9] = unsavedGroupDescription;
      $[10] = unsavedGroupSummary;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== isSearchPanel || $[13] !== openExistingTab || $[14] !== openNewGroupTab) {
      t8 = <DialogTabs isExistingActive={isSearchPanel} onExistingClick={openExistingTab} onNewClick={openNewGroupTab} />;
      $[12] = isSearchPanel;
      $[13] = openExistingTab;
      $[14] = openNewGroupTab;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    const t9 = isSearchPanel ? <div className="tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-iron-900/40 tw-p-3">{renderSearchPanel({
        allowGroupClear,
        defaultLabel,
        disabled,
        hasUnsavedGroup,
        onExistingGroupSelect,
        resultsLayout: "inline",
        selectedGroup
      })}</div> : <div className="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-iron-900/40 tw-p-3"><DialogNewGroupActions disabled={disabled} identityCount={identityCount} identityActive={isIdentityPanel} ruleCount={ruleCount} ruleActive={isRulePanel} onAddIdentity={() => togglePanel("identity", isIdentityPanel)} onAddRule={() => togglePanel("rule-list", displayedBuilder.panel === "rule-list")} />{displayedBuilder.panel === "identity" ? <CreateWaveInlineGroupExpandedPanel onCancel={onCancelPanel} cancelClassName="tw-mt-3" cancelLabel="Done"><CreateWaveInlineGroupIdentities identities={displayedBuilder.identities} onIdentitySelect={addIdentity} onRemove={removeIdentity} resultsLayout="inline" /></CreateWaveInlineGroupExpandedPanel> : null}{displayedBuilder.panel === "rule-list" ? <CreateWaveInlineGroupExpandedPanel onCancel={onCancelPanel} cancelLabel="Done"><CreateWaveInlineGroupRuleList disabled={disabled} onRuleOpen={openRule} /></CreateWaveInlineGroupExpandedPanel> : null}{displayedBuilder.panel === "rule-editor" && displayedBuilder.activeRule !== null ? <CreateWaveInlineGroupExpandedPanel onCancel={onCancelPanel} cancelLabel="Done"><CreateWaveInlineGroupRuleEditorPanel activeRule={displayedBuilder.activeRule} disabled={disabled} onRuleToggle={toggleRule}><CreateWaveInlineGroupRuleEditor draft={displayedBuilder.draft} activeRule={displayedBuilder.activeRule} onDraftChange={setDraft} /></CreateWaveInlineGroupRuleEditorPanel></CreateWaveInlineGroupExpandedPanel> : null}{showDraftFooter ? <CreateWaveInlineGroupDraftSummary draftSummary={draftSummary} isValid={isDraftValid} canResetDraft={canResetDraft} canCreateDraft={canCreateDraft} isCreating={isCreating} onClearAll={onClearAll} onCreateAndUse={onCreateAndUse} /> : null}</div>;
    let t10;
    if ($[16] !== panelRef || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
      t10 = <div ref={panelRef} className="tw-flex tw-flex-col tw-gap-4">{t7}{t8}{t9}</div>;
      $[16] = panelRef;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    return t10;
  }
  let t4;
  if ($[21] !== currentGroupLabel || $[22] !== unsavedGroupDescription || $[23] !== unsavedGroupSummary) {
    t4 = <CreateWaveInlineGroupHeader currentGroupLabel={currentGroupLabel} unsavedGroupDescription={unsavedGroupDescription} unsavedGroupSummary={unsavedGroupSummary} />;
    $[21] = currentGroupLabel;
    $[22] = unsavedGroupDescription;
    $[23] = unsavedGroupSummary;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== isIdentityPanel || $[26] !== togglePanel) {
    t5 = () => togglePanel("identity", isIdentityPanel);
    $[25] = isIdentityPanel;
    $[26] = togglePanel;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== isRulePanel || $[29] !== togglePanel) {
    t6 = () => togglePanel("rule-list", isRulePanel);
    $[28] = isRulePanel;
    $[29] = togglePanel;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== isSearchPanel || $[32] !== togglePanel) {
    t7 = () => togglePanel("search", isSearchPanel);
    $[31] = isSearchPanel;
    $[32] = togglePanel;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== addIdentity || $[35] !== displayedBuilder.identities || $[36] !== displayedBuilder.panel || $[37] !== onCancelPanel || $[38] !== removeIdentity) {
    t8 = displayedBuilder.panel === "identity" ? <CreateWaveInlineGroupExpandedPanel onCancel={onCancelPanel} cancelClassName="tw-mt-3"><CreateWaveInlineGroupIdentities identities={displayedBuilder.identities} onIdentitySelect={addIdentity} onRemove={removeIdentity} /></CreateWaveInlineGroupExpandedPanel> : null;
    $[34] = addIdentity;
    $[35] = displayedBuilder.identities;
    $[36] = displayedBuilder.panel;
    $[37] = onCancelPanel;
    $[38] = removeIdentity;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] !== canCreateDraft || $[41] !== canResetDraft || $[42] !== draftSummary || $[43] !== isCreating || $[44] !== isDraftValid || $[45] !== onClearAll || $[46] !== onCreateAndUse || $[47] !== showDraftFooter) {
    t9 = showDraftFooter ? <CreateWaveInlineGroupDraftSummary draftSummary={draftSummary} isValid={isDraftValid} canResetDraft={canResetDraft} canCreateDraft={canCreateDraft} isCreating={isCreating} onClearAll={onClearAll} onCreateAndUse={onCreateAndUse} /> : null;
    $[40] = canCreateDraft;
    $[41] = canResetDraft;
    $[42] = draftSummary;
    $[43] = isCreating;
    $[44] = isDraftValid;
    $[45] = onClearAll;
    $[46] = onCreateAndUse;
    $[47] = showDraftFooter;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  return <div ref={panelRef} className={`tw-relative tw-flex tw-flex-col tw-gap-5 tw-rounded-xl tw-border tw-border-solid tw-p-5 tw-transition-all tw-duration-300 ${isExpandedPanel ? "tw-border-white/10 tw-bg-iron-900 tw-shadow-2xl" : "tw-border-white/5 tw-bg-iron-900/60 tw-shadow-inner"}`}><div className="tw-relative tw-flex tw-flex-col tw-gap-5">{t4}<CreateWaveInlineGroupActions disabled={disabled} identityActive={isIdentityPanel} ruleActive={isRulePanel} searchActive={isSearchPanel} onAddIdentity={t5} onAddRule={t6} onUseExistingGroup={t7} />{t8}{displayedBuilder.panel === "rule-list" ? <CreateWaveInlineGroupExpandedPanel onCancel={onCancelPanel}><CreateWaveInlineGroupRuleList disabled={disabled} onRuleOpen={openRule} /></CreateWaveInlineGroupExpandedPanel> : null}{displayedBuilder.panel === "rule-editor" && displayedBuilder.activeRule !== null ? <CreateWaveInlineGroupExpandedPanel onCancel={onCancelPanel}><CreateWaveInlineGroupRuleEditorPanel activeRule={displayedBuilder.activeRule} disabled={disabled} onRuleToggle={toggleRule}><CreateWaveInlineGroupRuleEditor draft={displayedBuilder.draft} activeRule={displayedBuilder.activeRule} onDraftChange={setDraft} /></CreateWaveInlineGroupRuleEditorPanel></CreateWaveInlineGroupExpandedPanel> : null}{displayedBuilder.panel === "search" ? <CreateWaveInlineGroupExpandedPanel onCancel={onCancelPanel} cancelClassName="tw-mt-3">{renderSearchPanel({
          allowGroupClear,
          defaultLabel,
          disabled,
          hasUnsavedGroup,
          onExistingGroupSelect,
          selectedGroup
        })}</CreateWaveInlineGroupExpandedPanel> : null}{t9}</div></div>;
}